/**
 * Recipe editor (Steg 1c-i) — edit the scalar fields of a recipe with AUTOSAVE.
 *
 * Mirrors the prototype editor (recipe-d-seksjoner.html / mobile-«D»): a main
 * column (eyebrow, title, intro, category picker) and a right "Detaljer" panel
 * (total time, servings stepper, keywords). Section editing + drag-and-drop are
 * the next slices (1c-ii / 1c-iii); existing sections are shown read-only here.
 *
 * Save model — autosave (debounced ~1s):
 *  - "new" mode (`/recipes/new`): the recipe is NOT created until the first edit
 *    (so abandoning it leaves no empty draft). The first change POSTs; subsequent
 *    changes PATCH. We stay mounted on /recipes/new and keep the new id in state
 *    (no route change → no remount that would reload + clobber in-flight edits).
 *  - "edit" mode (`/recipes/:id/edit`): load the recipe, then PATCH on change.
 *
 * Authorization (UI mirror; the API + RLS are authoritative — see 1a deny tests):
 *  - Entry is role-gated: a read/comment member sees a forbidden message, not an
 *    editable form. New recipes are gated on the ACTIVE cookbook's role.
 *  - The autosave still handles a 403/404/401 defensively (server says no).
 *
 * Photo/OCR import (Steg 5) — REVIEW-BEFORE-SAVE:
 *  - `/recipes/new` can be entered with `location.state.importDraft` (a
 *    `RecipeImportDraft` from the photo-import flow — see
 *    `PhotoImportButton`). The form is SEEDED from it, but seeding uses a
 *    plain `setForm`/lazy `useState` initializer, never `update()` — so it
 *    does NOT schedule an autosave. Nothing is persisted merely by importing:
 *    the draft becomes a real recipe only once the user makes an edit (which
 *    schedules the normal create-on-first-edit autosave) OR clicks the
 *    explicit "Lagre oppskriften" action shown while reviewing an import.
 */

import {
  RECIPE_CATEGORIES,
  roleCanEditRecipes,
  type IngredientSection,
  type Recipe,
  type RecipeImportDraft,
  type StepSection,
  type UpdateRecipeInput,
} from "@tjommi/shared";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  EntitlementRequiredError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  createRecipe,
  fetchRecipe,
  fetchRecipes,
  requestImageUpload,
  requestVideoThumbnail,
  updateRecipe,
} from "../api";
import { PremiumNotice } from "../components/PremiumNotice";
import {
  SectionsEditor,
  emptyIngredientSection,
  emptyStepSection,
  pruneIngredientSections,
  pruneStepSections,
} from "../components/SectionsEditor";
import { useCookbook } from "../cookbook/CookbookContext";
import { UPLOAD_CONTENT_TYPE, resizeImageToJpeg, validateImageFile } from "../lib/imageUpload";

/**
 * Preset categories (single-select). Sourced from `@tjommi/shared` so the
 * picker, the import-prompt text and the server-side filter that rejects an
 * invented category all move together — a local copy here is exactly how the
 * imports ended up writing categories ("Hovedrett", "Hovedrett, Matbit") that
 * this picker never offered.
 */
const CATEGORIES = RECIPE_CATEGORIES;

const AUTOSAVE_DELAY_MS = 1000;

/** Sane caps for the two time-entry fields (Timer/Minutter). */
const MAX_HOURS = 99;
const MAX_MINUTES = 59;

/**
 * Split a persisted `totalTimeMinutes` into the two display strings — used
 * only when SEEDING the fields (initial mount / an existing recipe finishing
 * its load). Both empty ⇒ null; the fields otherwise track their own typed
 * text independently (see `combineTime`) so that clearing one field doesn't
 * silently invent a "0" in the other before the user has touched it.
 */
function deriveTimeStrings(totalTimeMinutes: number | null): { hours: string; minutes: string } {
  if (totalTimeMinutes == null) return { hours: "", minutes: "" };
  return {
    hours: String(Math.floor(totalTimeMinutes / 60)),
    minutes: String(totalTimeMinutes % 60),
  };
}

/**
 * Combine the two typed field strings into a single `totalTimeMinutes`.
 * Both blank ⇒ null (time stays optional); a blank field is otherwise treated
 * as 0 (e.g. "1" hour + "" minutes ⇒ 60). Callers only ever pass strings that
 * already passed `sanitizeTimeInput` (integers only, already clamped).
 */
function combineTime(hoursStr: string, minutesStr: string): number | null {
  if (hoursStr === "" && minutesStr === "") return null;
  const h = hoursStr === "" ? 0 : Number(hoursStr);
  const m = minutesStr === "" ? 0 : Number(minutesStr);
  return h * 60 + m;
}

/**
 * Validate + clamp a raw keystroke for a time field. Returns `null` when the
 * raw value must be REJECTED outright (ignored — the field keeps its previous
 * value): anything that isn't empty or a plain non-negative integer string
 * (no ".", "-", "e", etc.), which is how a decimal keystroke is kept from ever
 * reaching state or the save payload (the API 400s on a non-integer).
 */
function sanitizeTimeInput(raw: string, max: number): string | null {
  if (raw === "") return "";
  if (!/^\d+$/.test(raw)) return null;
  return String(Math.min(Number(raw), max));
}

interface EditorForm {
  title: string;
  intro: string;
  category: string | null;
  tags: string[];
  servings: number | null;
  totalTimeMinutes: number | null;
  ingredientSections: IngredientSection[];
  stepSections: StepSection[];
  /** Hero image object-storage key, or null. (Steg 3b.) */
  heroImageKey: string | null;
  /** Signed GET URL for `heroImageKey` (display-only; not sent on write). */
  heroImageUrl: string | null;
  /** External video link, or "" when none. (Video-link, Slice C.) */
  videoUrl: string;
  /** Stored preview-thumbnail object-storage key for `videoUrl`, or null. */
  videoThumbnailKey: string | null;
  /** Signed GET URL for `videoThumbnailKey` (display-only; not sent on write). */
  videoThumbnailUrl: string | null;
}

/**
 * `"entitlement"` is NOT a flavour of `"error"`: nothing is broken and nothing
 * is lost — the recipe simply wasn't created because the cookbook is at its
 * free limit. It therefore gets its own pill AND an inline `PremiumNotice`
 * (the explanation is far too long for the pill), and offers no "prøv igjen":
 * retrying the same save would hit the same limit.
 */
type SaveStatus = "idle" | "saving" | "saved" | "error" | "forbidden" | "entitlement";

function toForm(recipe: Recipe): EditorForm {
  return {
    title: recipe.title,
    intro: recipe.intro ?? "",
    category: recipe.category,
    tags: recipe.tags,
    servings: recipe.servings,
    totalTimeMinutes: recipe.totalTimeMinutes,
    // Seed an empty section so there's always a row to type into (pruned on save).
    ingredientSections: recipe.ingredientSections.length
      ? recipe.ingredientSections
      : [emptyIngredientSection()],
    stepSections: recipe.stepSections.length ? recipe.stepSections : [emptyStepSection()],
    heroImageKey: recipe.heroImageKey,
    heroImageUrl: recipe.heroImageUrl ?? null,
    videoUrl: recipe.videoUrl ?? "",
    videoThumbnailKey: recipe.videoThumbnailKey,
    videoThumbnailUrl: recipe.videoThumbnailUrl ?? null,
  };
}

/** A blank editor form (new recipe) — one empty section per card, ready to type. */
function emptyForm(): EditorForm {
  return {
    title: "",
    intro: "",
    category: null,
    tags: [],
    servings: null,
    totalTimeMinutes: null,
    ingredientSections: [emptyIngredientSection()],
    stepSections: [emptyStepSection()],
    heroImageKey: null,
    heroImageUrl: null,
    videoUrl: "",
    videoThumbnailKey: null,
    videoThumbnailUrl: null,
  };
}

/**
 * Seed the editor form from an import draft (photo or url — Steg 5) — the
 * same shape as `toForm`. IMPORTANT: seeding via this function must never
 * itself count as a user edit — the caller applies it with a plain
 * `setForm`/lazy `useState` initializer, NOT via `update()`, so no autosave
 * is scheduled (see the "review-before-save" docs on `RecipeEditorPage`).
 * Nothing is persisted merely by importing.
 *
 * `heroImageKey`: a url import may carry a server-minted hero image key (a
 * photo import never does — a photo of a page isn't itself a hero image).
 * It's carried into the form so it PERSISTS on save (see `doSave`/
 * `ensureRecipeId` below, which pass it on the CREATE payload). `heroImageUrl`
 * (and each step's `imageUrl`) is a signed, display-only GET url the API mints
 * fresh on every poll (`RecipeImportService.getJob`) — seeded here exactly
 * like `toForm` seeds them for an already-saved recipe, so the review screen
 * shows the imported hero/step images immediately, before anything is saved.
 * A url import carries a real hero preview; a photo import carries `null`
 * (no hero at all). Either way, `heroImageUrl`/step `imageUrl` are never sent
 * on save — only the underlying keys are (see `buildBasePayload`/`doSave`).
 *
 * `videoUrl`/`videoThumbnailKey`/`videoThumbnailUrl`: present only for a
 * VIDEO import (a photo/url import never sets these). Mirrors the hero
 * fields' exact contract — the signed `videoThumbnailUrl` is shown
 * immediately during review but never sent on save, only the key is (see
 * `buildBasePayload`).
 */
function fromImportDraft(draft: RecipeImportDraft): EditorForm {
  return {
    title: draft.title,
    intro: draft.intro ?? "",
    category: draft.category,
    tags: draft.tags,
    servings: draft.servings,
    totalTimeMinutes: draft.totalTimeMinutes,
    ingredientSections: draft.ingredientSections.length
      ? draft.ingredientSections
      : [emptyIngredientSection()],
    stepSections: draft.stepSections.length ? draft.stepSections : [emptyStepSection()],
    heroImageKey: draft.heroImageKey ?? null,
    heroImageUrl: draft.heroImageUrl ?? null,
    videoUrl: draft.videoUrl ?? "",
    videoThumbnailKey: draft.videoThumbnailKey ?? null,
    videoThumbnailUrl: draft.videoThumbnailUrl ?? null,
  };
}

/** Router `location.state` shape carrying an imported draft into `/recipes/new`. */
interface ImportDraftLocationState {
  importDraft?: RecipeImportDraft;
}

type LoadState =
  | { status: "ready"; recipe: Recipe | null }
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "unauthorized" }
  | { status: "forbidden"; recipeId: string | null }
  | { status: "error"; message: string };

export function RecipeEditorPage({
  accessToken,
  onSessionExpired,
  mode,
  autosaveDelayMs = AUTOSAVE_DELAY_MS,
  resizeImage = resizeImageToJpeg,
}: {
  accessToken: string;
  onSessionExpired: () => void;
  mode: "new" | "edit";
  /** Debounce before autosaving. Default 1s; tests pass a small value. */
  autosaveDelayMs?: number;
  /**
   * Resize/re-encode a picked file to a JPEG blob before upload. Real canvas
   * implementation by default; tests inject a fast deterministic stub (jsdom
   * cannot decode real image bytes via <canvas>/<Image>).
   */
  resizeImage?: (file: File) => Promise<Blob>;
}): React.JSX.Element {
  const { id: routeId } = useParams<{ id: string }>();
  const { active, cookbooks } = useCookbook();
  const location = useLocation();

  // A photo-import draft (Steg 5), if we were navigated here with one — only
  // meaningful in "new" mode. Read once; navigation to this route only ever
  // happens on mount (no re-navigation while staying mounted), so a plain
  // (non-memoized) read here is fine — it's only actually consumed by the
  // lazy useState initializers below, which run exactly once.
  const importDraft: RecipeImportDraft | null =
    mode === "new"
      ? ((location.state as ImportDraftLocationState | null)?.importDraft ?? null)
      : null;

  const [load, setLoad] = useState<LoadState>(
    mode === "edit" ? { status: "loading" } : { status: "ready", recipe: null },
  );
  const [form, setForm] = useState<EditorForm>(() =>
    importDraft ? fromImportDraft(importDraft) : emptyForm(),
  );
  // ---- Time entry (Timer/Minutter) ----
  // Tracked as their OWN typed text, seeded from `form.totalTimeMinutes` only
  // at mount and whenever a recipe finishes loading (see the load effect
  // below) — not re-derived on every render, so that clearing one field
  // doesn't get overwritten by a "0" derived back from the other's value
  // (see `combineTime`'s docs: clearing BOTH fields must yield null).
  const [timeHours, setTimeHours] = useState<string>(
    () => deriveTimeStrings(form.totalTimeMinutes).hours,
  );
  const [timeMinutes, setTimeMinutes] = useState<string>(
    () => deriveTimeStrings(form.totalTimeMinutes).minutes,
  );
  const [status, setStatus] = useState<SaveStatus>("idle");
  // The parsed 402 behind `status === "entitlement"` (the pill is too small to
  // explain a plan limit; the notice below the toolbar does the explaining).
  const [saveDenial, setSaveDenial] = useState<EntitlementRequiredError | null>(null);
  // True while an imported draft is shown for review and hasn't been saved
  // yet (by an edit or the explicit save button) — drives the review banner.
  // Seeding the form above does NOT flip this on its own; it only reflects
  // whether we STARTED from an import and nothing has persisted it yet.
  const [reviewingImport, setReviewingImport] = useState(importDraft != null);

  // Existing keywords across the owning cookbook — for the tag autocomplete, so
  // the user picks an existing tag instead of creating a near-duplicate. Only
  // fetched once we know the editor is editable (a new recipe in a writable
  // active cookbook, or an existing one resolved on load — set below).
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [tagSource, setTagSource] = useState<string | null>(() =>
    mode === "new" && roleCanEditRecipes(active.role) ? active.id : null,
  );

  // The recipe id + owning cookbook id, available to the async save closure.
  const [recipeId, setRecipeId] = useState<string | null>(
    mode === "edit" ? (routeId ?? null) : null,
  );
  const recipeIdRef = useRef<string | null>(recipeId);
  recipeIdRef.current = recipeId;
  const cookbookIdRef = useRef<string>(active.id);
  const formRef = useRef<EditorForm>(form);
  formRef.current = form;

  const savingRef = useRef(false);
  const pendingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Video link (Slice C) ----
  // The trimmed videoUrl we last completed a thumbnail fetch for (success OR
  // a null result both count) — drives the "ingen forhåndsvisning
  // tilgjengelig" note (shown only once we've actually tried for the CURRENT
  // url, not just because there's no thumbnail yet). Seeded from whatever the
  // form already knows (a loaded recipe or an import draft already carries
  // its own answer — no need to re-fetch). null on a network failure so the
  // next blur retries.
  const lastFetchedVideoUrlRef = useRef<string | null>(
    form.videoUrl.trim() === "" ? null : form.videoUrl.trim(),
  );
  const [videoThumbnailLoading, setVideoThumbnailLoading] = useState(false);

  // ---- Images (Steg 3b) ----
  // Hero: uploading/error are transient UI state, not part of the autosave
  // status machine (a failed image upload shouldn't show as a save error).
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroError, setHeroError] = useState<string | null>(null);
  // Per-step: keyed by the step's client id (only one step can upload at a time
  // in practice, but this keeps concurrent picks on different steps correct).
  const [stepUploading, setStepUploading] = useState<Record<string, boolean>>({});
  const [stepError, setStepError] = useState<Record<string, string | null>>({});

  // ---- Load (edit mode) ----
  useEffect(() => {
    if (mode !== "edit") return;
    if (!routeId) return;
    let ignore = false;
    setLoad({ status: "loading" });

    fetchRecipe(routeId, accessToken)
      .then((recipe) => {
        if (ignore) return;
        cookbookIdRef.current = recipe.cookbookId;
        const role = cookbooks.find((c) => c.id === recipe.cookbookId)?.role;
        if (!role || !roleCanEditRecipes(role)) {
          setLoad({ status: "forbidden", recipeId: recipe.id });
          return;
        }
        setForm(toForm(recipe));
        const derived = deriveTimeStrings(recipe.totalTimeMinutes);
        setTimeHours(derived.hours);
        setTimeMinutes(derived.minutes);
        setTagSource(recipe.cookbookId);
        // The loaded recipe already carries the server's own answer for its
        // videoUrl (a stored thumbnail, or confirmed none) — treat it as
        // already-checked so opening the editor doesn't re-fetch immediately.
        lastFetchedVideoUrlRef.current = recipe.videoUrl?.trim() || null;
        setLoad({ status: "ready", recipe });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) setLoad({ status: "unauthorized" });
        else if (err instanceof NotFoundError) setLoad({ status: "not-found" });
        else
          setLoad({ status: "error", message: err instanceof Error ? err.message : "Ukjent feil" });
      });

    return () => {
      ignore = true;
    };
  }, [mode, routeId, accessToken, cookbooks]);

  // ---- Existing keywords (tag autocomplete; best-effort) ----
  useEffect(() => {
    if (!tagSource) return;
    let ignore = false;
    fetchRecipes(tagSource, accessToken)
      .then((recipes) => {
        if (!ignore) setExistingTags([...new Set(recipes.flatMap((r) => r.tags))].sort());
      })
      .catch(() => undefined); // suggestions are a convenience, not load-bearing
    return () => {
      ignore = true;
    };
  }, [tagSource, accessToken]);

  // ---- Autosave ----

  /** The scalar/section fields shared by both the create and PATCH payloads. */
  const buildBasePayload = useCallback(
    (f: EditorForm): UpdateRecipeInput => ({
      intro: f.intro.trim() === "" ? null : f.intro,
      category: f.category,
      tags: f.tags,
      servings: f.servings,
      totalTimeMinutes: f.totalTimeMinutes,
      // Drop blank rows/sections so we never persist empty scaffolding.
      ingredientSections: pruneIngredientSections(f.ingredientSections),
      stepSections: pruneStepSections(f.stepSections),
      // Clearing the field sends null — the API clears the stored thumbnail
      // key along with it (video-link, Slice C). videoThumbnailUrl is never
      // sent (display-only, mirrors heroImageUrl's write-split).
      videoUrl: f.videoUrl.trim() === "" ? null : f.videoUrl,
      videoThumbnailKey: f.videoThumbnailKey,
    }),
    [],
  );

  const doSave = useCallback(
    async (force = false): Promise<void> => {
      if (savingRef.current) {
        pendingRef.current = true;
        return;
      }
      savingRef.current = true;
      pendingRef.current = false;
      setStatus("saving");
      // A new attempt supersedes the previous verdict — clear the notice so a
      // save that now succeeds doesn't leave a stale "krever Premium" behind.
      setSaveDenial(null);

      const f = formRef.current;
      const base = buildBasePayload(f);
      const trimmedTitle = f.title.trim();

      try {
        if (recipeIdRef.current == null) {
          // Don't create an empty "Ny oppskrift" draft: only create once there's a
          // title or real content. A lone category/servings/time isn't worth
          // persisting, so an opened-then-abandoned editor leaves nothing behind.
          // `force` (the explicit "Lagre oppskriften" import-review action)
          // bypasses this — an explicit save always persists, even an
          // unreadable-photo draft the user wants to keep as a bare title.
          const hasContent =
            trimmedTitle !== "" ||
            base.intro != null ||
            (base.tags?.length ?? 0) > 0 ||
            (base.ingredientSections?.length ?? 0) > 0 ||
            (base.stepSections?.length ?? 0) > 0;
          if (!hasContent && !force) {
            setStatus("idle");
            return;
          }
          // First save of a new recipe → create it (title required by the
          // API). Carry the form's heroImageKey too — this is how a
          // url-import draft's server-minted hero image (see
          // `fromImportDraft`'s docs) ends up persisted on the new recipe.
          const created = await createRecipe(
            cookbookIdRef.current,
            { ...base, title: trimmedTitle || "Ny oppskrift", heroImageKey: f.heroImageKey },
            accessToken,
          );
          recipeIdRef.current = created.id;
          setRecipeId(created.id);
          setReviewingImport(false);
        } else {
          // PATCH only includes a non-empty title (never clear it to "").
          const payload: UpdateRecipeInput = trimmedTitle ? { ...base, title: trimmedTitle } : base;
          await updateRecipe(recipeIdRef.current, payload, accessToken);
        }
        setStatus("saved");
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        if (err instanceof ForbiddenError) {
          setStatus("forbidden");
          return;
        }
        if (err instanceof EntitlementRequiredError) {
          // 402 — the cookbook is at the free recipe limit (only a CREATE can
          // hit this; editing an existing recipe is never refused, §2.2's soft
          // limit). Distinct from `forbidden` (403, weak role) on purpose.
          setSaveDenial(err);
          setStatus("entitlement");
          return;
        }
        setStatus("error");
      } finally {
        savingRef.current = false;
        // Coalesce changes that arrived while we were saving.
        if (pendingRef.current) {
          pendingRef.current = false;
          void doSave();
        }
      }
    },
    [accessToken, onSessionExpired, buildBasePayload],
  );

  /**
   * Ensure a real `recipeId` exists, creating the recipe (with current form
   * state) if this is still an unsaved "new" draft — so an image can be
   * uploaded against a real `POST /recipes/:id/images`. Reuses the exact same
   * `createRecipe` call `doSave` uses (same payload shape), so picking an image
   * on a brand-new draft counts as "content" that forces creation, same as
   * typing a title would. Returns the (possibly newly created) recipe id.
   */
  const ensureRecipeId = useCallback(async (): Promise<string> => {
    if (recipeIdRef.current != null) return recipeIdRef.current;
    const f = formRef.current;
    const base = buildBasePayload(f);
    const trimmedTitle = f.title.trim();
    const created = await createRecipe(
      cookbookIdRef.current,
      { ...base, title: trimmedTitle || "Ny oppskrift", heroImageKey: f.heroImageKey },
      accessToken,
    );
    recipeIdRef.current = created.id;
    setRecipeId(created.id);
    // Reflect anything the server defaulted/normalized (e.g. the title
    // fallback) back into the form so the UI and the persisted recipe agree.
    setForm((prev) => ({ ...prev, title: created.title }));
    return created.id;
  }, [accessToken, buildBasePayload]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void doSave(), autosaveDelayMs);
  }, [doSave, autosaveDelayMs]);

  /**
   * "Lagre oppskriften" — the explicit save action shown while reviewing an
   * imported draft (Steg 5). Saves IMMEDIATELY (no debounce) and, unlike the
   * normal autosave gate, persists even if the draft is otherwise "empty"
   * (`force=true`) — an explicit click is a clear enough signal on its own.
   */
  const saveImportedDraft = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    void doSave(true);
  }, [doSave]);

  // Flush a pending save on unmount so a quick exit doesn't drop the last edit.
  // Keyed on [] (true unmount only) + a ref to the latest doSave — keying on
  // `doSave` would re-run the cleanup on every render (doSave's identity changes
  // each render), firing a save storm.
  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        void doSaveRef.current();
      }
    };
  }, []);

  /** Update one field + schedule an autosave. */
  const update = useCallback(
    <K extends keyof EditorForm>(key: K, value: EditorForm[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      scheduleSave();
    },
    [scheduleSave],
  );

  /**
   * Timer/Minutter keystroke handlers: sanitize (integers only, clamped),
   * update the field's own local text, combine with the OTHER field's current
   * text into `totalTimeMinutes`, and push that through the normal `update()`
   * path so autosave still fires. An invalid keystroke (e.g. a decimal point)
   * is rejected outright — it never reaches state or the save payload; the
   * input's DOM value is corrected back synchronously (a controlled input's
   * `value` prop alone doesn't guarantee a same-value re-render happens before
   * the next paint).
   */
  const handleHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeTimeInput(e.target.value, MAX_HOURS);
      if (sanitized == null) {
        e.target.value = timeHours;
        return;
      }
      setTimeHours(sanitized);
      update("totalTimeMinutes", combineTime(sanitized, timeMinutes));
    },
    [update, timeHours, timeMinutes],
  );
  const handleMinutesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeTimeInput(e.target.value, MAX_MINUTES);
      if (sanitized == null) {
        e.target.value = timeMinutes;
        return;
      }
      setTimeMinutes(sanitized);
      update("totalTimeMinutes", combineTime(timeHours, sanitized));
    },
    [update, timeHours, timeMinutes],
  );

  // Section changes carry an optional `save` flag: live drag-reorder moves pass
  // `save=false` (no autosave churn mid-drag), the drop passes `save=true`.
  const changeIngredients = useCallback(
    (next: IngredientSection[], save = true) => {
      setForm((prev) => ({ ...prev, ingredientSections: next }));
      if (save) scheduleSave();
    },
    [scheduleSave],
  );
  const changeSteps = useCallback(
    (next: StepSection[], save = true) => {
      setForm((prev) => ({ ...prev, stepSections: next }));
      if (save) scheduleSave();
    },
    [scheduleSave],
  );

  // ---- Video link (Slice C) ----
  // Every keystroke updates the form (+ schedules the normal debounced
  // autosave, same as intro/title) — but the thumbnail fetch itself only
  // fires on blur (see `handleVideoUrlBlur`), never on every keystroke.
  // Clearing the field drops the local preview/key immediately (the API
  // clears the stored key server-side once the null reaches it).
  const handleVideoUrlChange = useCallback(
    (value: string) => {
      setForm((prev) => {
        if (value.trim() === "") {
          lastFetchedVideoUrlRef.current = null;
          return { ...prev, videoUrl: "", videoThumbnailKey: null, videoThumbnailUrl: null };
        }
        return { ...prev, videoUrl: value };
      });
      scheduleSave();
    },
    [scheduleSave],
  );

  /**
   * On blur: if the (trimmed, non-empty) url differs from the one we last
   * fetched a thumbnail for, ask the API for a preview still. NEVER an error
   * response for an unfetchable thumbnail (see `requestVideoThumbnail`'s
   * docs) — a genuine failure (network/5xx) is swallowed as best-effort so
   * typing a video link never blocks the recipe itself from saving; the next
   * blur retries (the ref is only advanced on a completed attempt).
   */
  const handleVideoUrlBlur = useCallback(async (): Promise<void> => {
    const url = formRef.current.videoUrl.trim();
    if (url === "" || url === lastFetchedVideoUrlRef.current) return;
    setVideoThumbnailLoading(true);
    try {
      const { videoThumbnailKey, videoThumbnailUrl } = await requestVideoThumbnail(
        cookbookIdRef.current,
        url,
        accessToken,
      );
      lastFetchedVideoUrlRef.current = url;
      setForm((prev) => ({ ...prev, videoThumbnailKey, videoThumbnailUrl }));
      scheduleSave();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      // best-effort — leave the link as typed; it still saves without a preview.
    } finally {
      setVideoThumbnailLoading(false);
    }
  }, [accessToken, onSessionExpired, scheduleSave]);

  // ---- Image upload (Steg 3b) ----
  // Shared sequence (both hero and per-step): validate → resize/re-encode to
  // JPEG → ensure a real recipeId exists → request a presigned PUT URL → PUT
  // the bytes → PATCH the recipe with the returned key (or null on removal).
  // The PATCH/POST response already carries a freshly-signed *Url, so it's
  // applied straight to local state — no extra re-fetch needed. A 401 defers
  // to the normal session-expiry flow; other failures show a short inline
  // Norwegian error and never touch the (unrelated) autosave status pill.
  const handleHeroPick = useCallback(
    async (file: File): Promise<void> => {
      setHeroError(null);
      setHeroUploading(true);
      try {
        const validationError = validateImageFile(file);
        if (validationError) {
          setHeroError(validationError);
          return;
        }
        const jpegBlob = await resizeImage(file);
        const id = await ensureRecipeId();
        const { key, uploadUrl } = await requestImageUpload(id, UPLOAD_CONTENT_TYPE, accessToken);
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: jpegBlob,
          headers: { "Content-Type": UPLOAD_CONTENT_TYPE },
        });
        if (!putRes.ok) {
          setHeroError("Opplasting feilet. Prøv igjen.");
          return;
        }
        const updated = await updateRecipe(id, { heroImageKey: key }, accessToken);
        setForm((prev) => ({
          ...prev,
          heroImageKey: updated.heroImageKey,
          heroImageUrl: updated.heroImageUrl ?? null,
        }));
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        if (err instanceof EntitlementRequiredError) {
          // Picking an image on a brand-new draft CREATES the recipe first
          // (`ensureRecipeId`), so this path can hit the free recipe limit too.
          // It is a plan limit, not an upload failure — explain it in the
          // notice at the top rather than as a red line under the picker.
          // The pill moves with it: the recipe was NOT created, so leaving a
          // stale "Lagret" next to the Premium notice would contradict it.
          setSaveDenial(err);
          setStatus("entitlement");
          return;
        }
        setHeroError(err instanceof Error ? err.message : "Opplasting feilet. Prøv igjen.");
      } finally {
        setHeroUploading(false);
      }
    },
    [accessToken, ensureRecipeId, onSessionExpired, resizeImage],
  );

  const handleHeroRemove = useCallback(async (): Promise<void> => {
    const id = recipeIdRef.current;
    if (id == null) return;
    setHeroError(null);
    setHeroUploading(true);
    try {
      const updated = await updateRecipe(id, { heroImageKey: null }, accessToken);
      setForm((prev) => ({
        ...prev,
        heroImageKey: updated.heroImageKey,
        heroImageUrl: updated.heroImageUrl ?? null,
      }));
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setHeroError("Kunne ikke fjerne bildet. Prøv igjen.");
    } finally {
      setHeroUploading(false);
    }
  }, [accessToken, onSessionExpired]);

  /**
   * Update one step's `image` key in the current stepSections (by client id,
   * not [section,row] index — indices can shift under a concurrent drag/edit)
   * and mirror the freshly-signed `imageUrl` from the server response. Does
   * NOT schedule an autosave — the PATCH that set the key already persisted it.
   */
  const applyStepImage = useCallback(
    (stepId: string, image: string | null, imageUrl: string | null) => {
      setForm((prev) => ({
        ...prev,
        stepSections: prev.stepSections.map((sec) => ({
          ...sec,
          items: sec.items.map((it) => (it.id === stepId ? { ...it, image, imageUrl } : it)),
        })),
      }));
    },
    [],
  );

  /** Send the FULL current stepSections array with one step's image key changed. */
  const patchStepImage = useCallback(
    async (stepId: string, key: string | null): Promise<Recipe> => {
      const id = recipeIdRef.current;
      if (id == null) throw new Error("Oppskriften finnes ikke ennå.");
      const nextSteps = formRef.current.stepSections.map((sec) => ({
        ...sec,
        items: sec.items.map((it) => (it.id === stepId ? { ...it, image: key } : it)),
      }));
      return updateRecipe(id, { stepSections: pruneStepSections(nextSteps) }, accessToken);
    },
    [accessToken],
  );

  const handleStepImagePick = useCallback(
    async (stepId: string, file: File): Promise<void> => {
      setStepError((prev) => ({ ...prev, [stepId]: null }));
      setStepUploading((prev) => ({ ...prev, [stepId]: true }));
      try {
        const validationError = validateImageFile(file);
        if (validationError) {
          setStepError((prev) => ({ ...prev, [stepId]: validationError }));
          return;
        }
        const jpegBlob = await resizeImage(file);
        await ensureRecipeId();
        const id = recipeIdRef.current!;
        const { key, uploadUrl } = await requestImageUpload(id, UPLOAD_CONTENT_TYPE, accessToken);
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: jpegBlob,
          headers: { "Content-Type": UPLOAD_CONTENT_TYPE },
        });
        if (!putRes.ok) {
          setStepError((prev) => ({ ...prev, [stepId]: "Opplasting feilet. Prøv igjen." }));
          return;
        }
        const updated = await patchStepImage(stepId, key);
        const savedStep = updated.stepSections
          .flatMap((s) => s.items)
          .find((it) => it.id === stepId);
        applyStepImage(stepId, key, savedStep?.imageUrl ?? null);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        if (err instanceof EntitlementRequiredError) {
          // Same as the hero path: the implicit create behind a first image
          // upload can hit the free recipe limit — and the pill must agree
          // with the notice rather than still claiming "Lagret".
          setSaveDenial(err);
          setStatus("entitlement");
          return;
        }
        setStepError((prev) => ({
          ...prev,
          [stepId]: err instanceof Error ? err.message : "Opplasting feilet. Prøv igjen.",
        }));
      } finally {
        setStepUploading((prev) => ({ ...prev, [stepId]: false }));
      }
    },
    [accessToken, applyStepImage, ensureRecipeId, onSessionExpired, patchStepImage, resizeImage],
  );

  const handleStepImageRemove = useCallback(
    async (stepId: string): Promise<void> => {
      setStepError((prev) => ({ ...prev, [stepId]: null }));
      setStepUploading((prev) => ({ ...prev, [stepId]: true }));
      try {
        const updated = await patchStepImage(stepId, null);
        const savedStep = updated.stepSections
          .flatMap((s) => s.items)
          .find((it) => it.id === stepId);
        applyStepImage(stepId, null, savedStep?.imageUrl ?? null);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setStepError((prev) => ({ ...prev, [stepId]: "Kunne ikke fjerne bildet. Prøv igjen." }));
      } finally {
        setStepUploading((prev) => ({ ...prev, [stepId]: false }));
      }
    },
    [applyStepImage, onSessionExpired, patchStepImage],
  );

  // ---- Guards / non-editable states ----
  if (mode === "new" && !roleCanEditRecipes(active.role)) {
    return <Forbidden backTo="/" />;
  }
  if (load.status === "loading") {
    return (
      <main className="app-main">
        <div className="feed-state" data-testid="editor-loading">
          <p>Laster editor …</p>
        </div>
      </main>
    );
  }
  if (load.status === "unauthorized") {
    return (
      <main className="app-main">
        <div className="feed-state feed-error" data-testid="editor-unauthorized">
          <h2>Økten er utløpt</h2>
          <p>Du må logge inn på nytt.</p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: "16px" }}
            data-testid="editor-relogin"
            onClick={onSessionExpired}
          >
            Logg inn igjen
          </button>
        </div>
      </main>
    );
  }
  if (load.status === "not-found") {
    return (
      <main className="app-main">
        <div className="feed-state" data-testid="editor-not-found">
          <h2>Fant ikke oppskriften</h2>
          <p>Oppskriften finnes ikke, eller du har ikke tilgang til den.</p>
          <Link to="/" className="btn btn-ghost" style={{ marginTop: "16px" }}>
            ← Tilbake til kokeboken
          </Link>
        </div>
      </main>
    );
  }
  if (load.status === "forbidden") {
    return <Forbidden backTo={load.recipeId ? `/recipes/${load.recipeId}` : "/"} />;
  }
  if (load.status === "error") {
    return (
      <main className="app-main">
        <div className="feed-state feed-error" data-testid="editor-error">
          <h2>Kunne ikke åpne editoren</h2>
          <p>{load.message}</p>
        </div>
      </main>
    );
  }

  // ---- Editor (ready) ----
  const doneTo = recipeId ? `/recipes/${recipeId}` : "/";

  return (
    <main className="app-main">
      {/* Editor toolbar: eyebrow + autosave status + done */}
      <div className="editor-bar">
        <span className="eyebrow">{mode === "new" ? "Ny oppskrift" : "Rediger oppskrift"}</span>
        <span className="top-spacer" />
        <SaveIndicator status={status} onRetry={() => void doSave()} />
        <Link to={doneTo} className="btn btn-ghost" data-testid="editor-done">
          Ferdig
        </Link>
      </div>

      {/* 402 on save (abonnement Fase 1): the cookbook is at the free recipe
          limit, so this draft was NOT created. Shown at the top of the editor
          — everything typed stays on screen and is re-saved automatically if
          the limit goes away. */}
      {saveDenial && <PremiumNotice denial={saveDenial} testId="editor-premium" />}

      {/* Photo-import review banner (Steg 5) — shown only until the imported
          draft is saved (by an edit, which autosaves, or this explicit
          action). Nothing was persisted by the import itself. */}
      {reviewingImport && recipeId == null && (
        <div className="import-review-banner" data-testid="import-review-banner">
          <p>Oppskriften er importert. Se gjennom og rett det som trengs før du lagrer.</p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="import-save"
            onClick={saveImportedDraft}
          >
            Lagre oppskriften
          </button>
        </div>
      )}

      <div className="editor-grid">
        {/* Main column */}
        <div className="editor-main">
          <input
            className="title-input"
            placeholder="Tittel på oppskriften …"
            aria-label="Tittel"
            data-testid="editor-title"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
          />
          <textarea
            className="sub-input"
            rows={2}
            placeholder="Kort ingress …"
            aria-label="Ingress"
            data-testid="editor-intro"
            value={form.intro}
            onChange={(e) => update("intro", e.target.value)}
          />
          <div className="cat-grid" role="group" aria-label="Kategori">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={`cat-btn${form.category === c ? " is-on" : ""}`}
                data-testid={`editor-cat-${c}`}
                aria-pressed={form.category === c}
                onClick={() => update("category", form.category === c ? null : c)}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Hero image (Steg 3b) */}
          <HeroImagePicker
            imageUrl={form.heroImageUrl}
            uploading={heroUploading}
            error={heroError}
            onPick={(file) => void handleHeroPick(file)}
            onRemove={() => void handleHeroRemove()}
          />

          {/* Video link + preview (Slice C) */}
          <div className="video-link-field">
            <input
              className="input"
              type="url"
              placeholder="Lim inn en videolenke (valgfritt) …"
              aria-label="Video-lenke"
              data-testid="editor-video-url"
              value={form.videoUrl}
              onChange={(e) => handleVideoUrlChange(e.target.value)}
              onBlur={() => void handleVideoUrlBlur()}
            />
            {videoThumbnailLoading && (
              <p className="video-link-hint" data-testid="video-thumbnail-loading">
                Henter forhåndsvisning …
              </p>
            )}
            {!videoThumbnailLoading && form.videoThumbnailUrl && (
              <img
                src={form.videoThumbnailUrl}
                alt=""
                className="video-link-preview"
                data-testid="video-thumbnail-preview"
              />
            )}
            {!videoThumbnailLoading &&
              !form.videoThumbnailUrl &&
              form.videoUrl.trim() !== "" &&
              lastFetchedVideoUrlRef.current === form.videoUrl.trim() && (
                <p className="video-link-hint" data-testid="video-thumbnail-none">
                  Ingen forhåndsvisning tilgjengelig.
                </p>
              )}
          </div>

          {/* Editable ingredient + method sections (Steg 1c-ii, images in 3b). */}
          <SectionsEditor
            ingredientSections={form.ingredientSections}
            stepSections={form.stepSections}
            onIngredientChange={changeIngredients}
            onStepChange={changeSteps}
            stepUploading={stepUploading}
            stepErrors={stepError}
            onStepImagePick={(stepId, file) => void handleStepImagePick(stepId, file)}
            onStepImageRemove={(stepId) => void handleStepImageRemove(stepId)}
          />
        </div>

        {/* Right: Detaljer panel */}
        <aside className="editor-side">
          <div className="ai-placeholder" data-testid="ai-placeholder">
            <div className="ai-spark" aria-hidden="true">
              ✦
            </div>
            <div>
              <div className="ai-t">Sjef-assistenten</div>
              <div className="ai-s">Import & strukturering — kommer i Steg 5</div>
            </div>
          </div>

          <div className="card card-pad">
            <h3 className="detail-h">Detaljer</h3>

            <div className="detail-item">
              <span className="mi-ic" aria-hidden="true">
                ⏱
              </span>
              <div className="detail-field">
                <label className="mi-k" htmlFor="editor-time-hours">
                  Timer
                </label>
                <input
                  id="editor-time-hours"
                  className="detail-num"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  data-testid="editor-time-hours"
                  value={timeHours}
                  onChange={handleHoursChange}
                />
              </div>
              <div className="detail-field">
                <label className="mi-k" htmlFor="editor-time-minutes">
                  Minutter
                </label>
                <input
                  id="editor-time-minutes"
                  className="detail-num"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  data-testid="editor-time-minutes"
                  value={timeMinutes}
                  onChange={handleMinutesChange}
                />
              </div>
            </div>

            <div className="detail-item">
              <span className="mi-ic" aria-hidden="true">
                🍽
              </span>
              <div className="detail-field">
                <span className="mi-k">Porsjoner</span>
                <span className="mi-v" data-testid="editor-servings">
                  {form.servings ?? "—"}
                </span>
              </div>
              <span className="stepper">
                <button
                  type="button"
                  aria-label="Færre porsjoner"
                  data-testid="servings-dec"
                  onClick={() =>
                    update(
                      "servings",
                      form.servings == null ? null : form.servings <= 1 ? null : form.servings - 1,
                    )
                  }
                >
                  −
                </button>
                <button
                  type="button"
                  aria-label="Flere porsjoner"
                  data-testid="servings-inc"
                  onClick={() => update("servings", (form.servings ?? 0) + 1)}
                >
                  ＋
                </button>
              </span>
            </div>

            <div className="detail-tags">
              <div className="label">Nøkkelord</div>
              <TagEditor
                tags={form.tags}
                suggestions={existingTags}
                onChange={(tags) => update("tags", tags)}
              />
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

/**
 * Hero image control (Steg 3b): replaces the striped placeholder directly.
 * No image yet → the whole striped dropzone is a click target that opens a
 * file picker. An image set → it fills the same container (object-fit:
 * cover), with a small "✕" remove control shown on hover/focus.
 */
function HeroImagePicker({
  imageUrl,
  uploading,
  error,
  onPick,
  onRemove,
}: {
  imageUrl: string | null;
  uploading: boolean;
  error: string | null;
  onPick: (file: File) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) onPick(file);
  }

  return (
    <div className="editor-hero-wrap">
      <button
        type="button"
        className={`editor-dropzone${imageUrl ? " has-image" : ""}`}
        data-testid="hero-picker"
        aria-label={imageUrl ? "Bytt hovedbilde" : "Legg til hovedbilde"}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="editor-hero-img" data-testid="hero-image" />
        ) : (
          <span className="mono">{uploading ? "Laster opp …" : "＋ Legg til bilde"}</span>
        )}
        {uploading && imageUrl && <span className="hero-uploading-overlay">Laster opp …</span>}
      </button>
      {imageUrl && !uploading && (
        <button
          type="button"
          className="hero-remove-btn"
          data-testid="hero-remove"
          aria-label="Fjern hovedbilde"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          ✕
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="visually-hidden"
        data-testid="hero-file-input"
        onChange={handleChange}
      />
      {error && (
        <p className="image-error" data-testid="hero-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Autosave status pill (mirrors the prototype's "Lagret automatisk" chip). */
function SaveIndicator({
  status,
  onRetry,
}: {
  status: SaveStatus;
  onRetry: () => void;
}): React.JSX.Element {
  if (status === "saving") {
    return (
      <span className="save-state" data-testid="save-status">
        <span className="pulse saving" /> Lagrer …
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="save-state" data-testid="save-status">
        <span className="pulse" /> Lagret
      </span>
    );
  }
  if (status === "forbidden") {
    return (
      <span className="save-state is-error" data-testid="save-status">
        Ingen skrivetilgang
      </span>
    );
  }
  if (status === "entitlement") {
    // No retry affordance: the next identical save would be refused again.
    // The inline `PremiumNotice` carries the explanation.
    return (
      <span className="save-state is-error" data-testid="save-status">
        Ikke lagret · krever Premium
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="save-state is-error" data-testid="save-status">
        Kunne ikke lagre ·{" "}
        <button type="button" className="link-btn" data-testid="save-retry" onClick={onRetry}>
          prøv igjen
        </button>
      </span>
    );
  }
  return <span className="save-state" data-testid="save-status" />;
}

/**
 * Inline keyword editor: chips with remove + an "add" combobox that suggests
 * EXISTING keywords from the cookbook (so you reuse "barnevennlig" instead of
 * creating a near-duplicate). Suggestions are filtered by what you type and
 * exclude keywords already on this recipe.
 */
function TagEditor({
  tags,
  suggestions,
  onChange,
}: {
  tags: string[];
  suggestions: string[];
  onChange: (tags: string[]) => void;
}): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  function add(tag: string): void {
    const t = tag.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setDraft("");
    setAdding(false);
  }
  function cancel(): void {
    setDraft("");
    setAdding(false);
  }

  // Existing keywords not yet on this recipe, narrowed by the typed text.
  const q = draft.trim().toLowerCase();
  const available = suggestions.filter((s) => !tags.includes(s));
  const matches = (q ? available.filter((s) => s.toLowerCase().includes(q)) : available).slice(
    0,
    8,
  );

  return (
    <div className="tag-row" data-testid="editor-tags">
      {tags.map((t) => (
        <button
          key={t}
          type="button"
          className="chip is-on"
          data-testid={`editor-tag-${t}`}
          aria-label={`Fjern ${t}`}
          onClick={() => onChange(tags.filter((x) => x !== t))}
        >
          {t} ✕
        </button>
      ))}
      {adding ? (
        <div className="tag-combobox">
          <input
            className="input tag-input"
            autoFocus
            placeholder="Søk eller skriv nytt …"
            aria-label="Nytt nøkkelord"
            data-testid="tag-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add(draft);
              else if (e.key === "Escape") cancel();
            }}
            // Cancel on blur so a half-typed near-duplicate isn't auto-added;
            // suggestion clicks use onMouseDown (below) so they fire before blur.
            onBlur={cancel}
          />
          {matches.length > 0 && (
            <div className="tag-suggestions" role="listbox" data-testid="tag-suggestions">
              {matches.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="tag-suggestion"
                  data-testid={`tag-suggestion-${s}`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus → onBlur won't pre-empt the pick
                    add(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="tag-add-btn"
          data-testid="tag-add"
          onClick={() => setAdding(true)}
        >
          ＋ Legg til
        </button>
      )}
    </div>
  );
}

function Forbidden({ backTo }: { backTo: string }): React.JSX.Element {
  return (
    <main className="app-main">
      <div className="feed-state" data-testid="editor-forbidden">
        <h2>Ingen skrivetilgang</h2>
        <p>Du har ikke rettigheter til å redigere i denne kokeboken.</p>
        <Link to={backTo} className="btn btn-ghost" style={{ marginTop: "16px" }}>
          ← Tilbake
        </Link>
      </div>
    </main>
  );
}
